import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useThemeMode } from '../../_metronic/partials'
import Swal from 'sweetalert2'
import axios from 'axios'
import { Tooltip } from 'react-tooltip'
import { useParams, useNavigate } from 'react-router-dom'
import { HelpDrawer } from '../../_metronic/partials/layout/help-drawer/HelpDrawer'
import { PageTitle } from '../../_metronic/layout/core'
import userReadableISPPMap from '../static/userReadableISPPMap.json'
import categoryMap from '../static/categoryMap.json'
import isppDescriptions from '../static/isppDescriptions.json'
import ViolinPlot from '../components/Plotly'
import Modal from '../components/Modal'
const VariantSearchPage = () => {
  const { variant } = useParams() // URL'den gelen variant parametresi
  const navigate = useNavigate() // Yönlendirme için kullanılıyor
  const serviceUrl = process.env.REACT_APP_VAMPPSCORE_BACKEND_BASE_URL || ''
  const [plotError, setPlotError] = useState(null)
  const [maxHeight, setMaxHeight] = useState('calc(100vh - 330px)')

  const updateHeight = () => {
    setMaxHeight(`${window.innerHeight - 355}px`)
  }

  useEffect(() => {
    // İlk yükleme sırasında yükseklik hesaplaması yap
    updateHeight()

    // Pencere boyutu değiştiğinde yükseklik güncelle
    window.addEventListener('resize', updateHeight)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  useEffect(() => {
    const targetNode = document.getElementById('kt_app_sidebar')
    if (!targetNode) return

    const resizeObserver = new ResizeObserver(() => {
      updateHeight()
    })

    resizeObserver.observe(targetNode)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const [currentHeader, setCurrentHeader] = useState('Best ISPP')
  const containerRef = useRef(null)
  const bestIsppRef = useRef(null)
  const advancedRef = useRef(null)

  const [variantData, setVariantData] = useState(null)
  const [variantISPPData, setVariantISPPData] = useState(null)
  const [allISPPData, setAllISPPData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [bestISPPs, setBestISPPs] = useState([])
  const [error, setError] = useState(null)
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedRankScore, setSelectedRankScore] = useState(0)
  const [selectedISPP, setSelectedISPP] = useState(null)
  const [hidenegative, setHidenegative] = useState(false)
  const [violinData, setViolinData] = useState(null)
  const [sortStates, setSortStates] = useState({
    ISPP: 0,
    predictionAccuracy: 1, // Default olarak burası 1 olacak
    score: 0,
    rankScore: 0,
    category: 0,
  })

  const categoryColors = {
    A: '#e59b39',
    B: '#101f3d',
    C: '#e93645',
    D: '#3c8bc6',
  }

  // Scroll olayını dinlemek için useLayoutEffect kullanıyoruz
  useLayoutEffect(() => {
    const container = containerRef.current
    const handleScroll = () => {
      if (!container || !bestIsppRef.current || !advancedRef.current) return

      // Container ve diğer başlıkların konumlarını alıyoruz
      const containerTop = container.getBoundingClientRect().top
      const advancedTop = advancedRef.current.getBoundingClientRect().top

      // Kaydırma ile başlıkları değiştiriyoruz
      if (advancedTop <= containerTop + 50) {
        setCurrentHeader('Other ISPP')
      } else {
        setCurrentHeader('Best ISPP')
      }
    }

    if (container) {
      container.addEventListener('scroll', handleScroll)
      console.log('Scroll event listener added')
    }
  }, [variantISPPData]) // useLayoutEffect yalnızca bir kez çalışacak şekilde

  const handleSort = (column) => {
    setSortStates((prevState) => {
      const newStates = { ...prevState }

      // Tıklanan kolonun durumunu döngüsel olarak değiştir (0 → 1 → 2 → 1)
      newStates[column] = prevState[column] === 2 ? 1 : prevState[column] + 1

      // Diğer kolonları sıfırla
      Object.keys(newStates).forEach((key) => {
        if (key !== column) {
          newStates[key] = 0
        }
      })

      return newStates
    })
  }

  const categorizeISPPs = (data) => {
    const categorizedData = { A: [], B: [], C: [], D: [] }

    data.forEach((item) => {
      for (const [category, isppList] of Object.entries(categoryMap)) {
        if (isppList.includes(item.ispp)) {
          categorizedData[category].push(item)
          break
        }
      }
    })

    return categorizedData
  }

  const calculateGradientBackground = (geneScore, maxGeneScore, categoryColor) => {
    if (geneScore < 0) {
      return '#d3d3d3' // Gri renk
    }
    if (categoryColor === undefined) {
      return '#000000' // Gri renk
    }
    const minimumColors = {
      A: [253, 235, 236], // Kırmızı (en düşük)
      B: [172, 189, 232], // Mavi (en düşük)
      C: [234, 208, 173], // Sarı (en düşük)
      D: [198, 221, 238], // Açık mavi (en düşük)
    }

    const category = Object.keys(categoryColors).find(
      (key) => categoryColors[key].toLowerCase() === categoryColor.toLowerCase()
    )
    const [minR, minG, minB] = minimumColors[category] || [0, 0, 0]

    const r = parseInt(categoryColor.slice(1, 3), 16)
    const g = parseInt(categoryColor.slice(3, 5), 16)
    const b = parseInt(categoryColor.slice(5, 7), 16)

    const normalizedScore = Math.max(0, Math.min(1, geneScore / maxGeneScore))

    const adjustedR = Math.round(minR + (r - minR) * normalizedScore)
    const adjustedG = Math.round(minG + (g - minG) * normalizedScore)
    const adjustedB = Math.round(minB + (b - minB) * normalizedScore)

    return `rgb(${adjustedR}, ${adjustedG}, ${adjustedB})`
  }
  function sortDataByGeneScore(data, ascending = true) {
    return data
      .slice()
      .sort((a, b) => (ascending ? a.geneScore - b.geneScore : b.geneScore - a.geneScore))
  }

  function sortDataAlphabetically(data, ascending = true) {
    return data
      .slice()
      .sort((a, b) => (ascending ? a.ispp.localeCompare(b.ispp) : b.ispp.localeCompare(a.ispp)))
  }

  function sortDataByScore(data, ascending = true) {
    return data.slice().sort((a, b) => {
      const aScore = isNaN(a.score) || a.score === undefined ? -Infinity : a.score
      const bScore = isNaN(b.score) || b.score === undefined ? -Infinity : b.score

      return ascending ? aScore - bScore : bScore - aScore
    })
  }

  function sortDataByRankScore(data, ascending = true) {
    return data.slice().sort((a, b) => {
      const aRankScore = isNaN(a.rankscore) || a.rankscore === undefined ? -Infinity : a.rankscore
      const bRankScore = isNaN(b.rankscore) || b.rankscore === undefined ? -Infinity : b.rankscore

      return ascending ? aRankScore - bRankScore : bRankScore - aRankScore
    })
  }

  function sortDataByCategory(data, ascending = true) {
    const categoryOrder = {
      B: 0, // Amino Acid Conservation
      A: 1, // Nucleotide Conservation
      C: 2, // Multi-Parameter
      D: 3, // Multi-Score
    }

    return data.slice().sort((a, b) => {
      const aOrder = categoryOrder[a.category] ?? Infinity
      const bOrder = categoryOrder[b.category] ?? Infinity

      return ascending ? aOrder - bOrder : bOrder - aOrder
    })
  }

  useEffect(() => {
    if (!variantISPPData) return

    let sortedData = variantISPPData.slice()
    let sortedBestIspps = bestISPPs.slice()
    switch (true) {
      case sortStates.ISPP === 1:
        sortedData = sortDataAlphabetically(sortedData)
        sortedBestIspps = sortDataAlphabetically(sortedBestIspps)
        break

      case sortStates.ISPP === 2:
        sortedData = sortDataAlphabetically(sortedData, false)
        sortedBestIspps = sortDataAlphabetically(sortedBestIspps, false)
        break

      case sortStates.predictionAccuracy === 1:
        sortedData = sortDataByGeneScore(sortedData, false)
        sortedBestIspps = sortDataByGeneScore(sortedBestIspps, false)
        break

      case sortStates.predictionAccuracy === 2:
        sortedData = sortDataByGeneScore(sortedData)
        sortedBestIspps = sortDataByGeneScore(sortedBestIspps)
        break

      case sortStates.score === 1:
        sortedData = sortDataByScore(sortedData)
        sortedBestIspps = sortDataByScore(sortedBestIspps)
        break

      case sortStates.score === 2:
        sortedData = sortDataByScore(sortedData, false)
        sortedBestIspps = sortDataByScore(sortedBestIspps, false)
        break

      case sortStates.rankScore === 1:
        sortedData = sortDataByRankScore(sortedData)
        sortedBestIspps = sortDataByRankScore(sortedBestIspps)
        break

      case sortStates.rankScore === 2:
        sortedData = sortDataByRankScore(sortedData, false)
        sortedBestIspps = sortDataByRankScore(sortedBestIspps, false)
        break

      case sortStates.category === 1:
        sortedData = sortDataByCategory(sortedData)
        sortedBestIspps = sortDataByCategory(sortedBestIspps)
        break

      case sortStates.category === 2:
        sortedData = sortDataByCategory(sortedData, false)
        sortedBestIspps = sortDataByCategory(sortedBestIspps, false)
        break

      default:
        sortedData = sortDataByGeneScore(sortedData, false)
        sortedBestIspps = sortDataByGeneScore(sortedBestIspps, false)
        break
    }
    setVariantISPPData(sortedData)
    setBestISPPs(sortedBestIspps)
  }, [sortStates])

  const handleClickISPP = async (ispp, rankScore) => {
    setPlotError(null)
    setViolinData(null)
    setSelectedISPP(ispp)
    try {
      const clinvar = await axios.get(
        `${serviceUrl}/clinvar/gene/${variantData?.Gene.toLocaleUpperCase()}/${ispp}`,
        {
          headers: { 'Content-Type': 'application/json', Accept: '*' },
        }
      )
      setViolinData(clinvar.data)
      setSelectedISPP(ispp)
      setSelectedRankScore(Number.isNaN(rankScore) ? 0 : rankScore)
    } catch (error) {
      setPlotError('Error fetching data for ISPP')
    }
  }

  const fetchVariantData = async () => {
    if (!variant) return

    setLoading(true)
    try {
      const response = await axios.get(`${serviceUrl}/vamppscore/variant/${variant}`, {
        headers: { 'Content-Type': 'application/json', Accept: '*' },
      })
      const responsee = await axios.get(`${serviceUrl}/dbnsfp/variant/${variant}`, {
        headers: { 'Content-Type': 'application/json', Accept: '*' },
      })
      console.log(responsee, 'responsee')

      const data = response.data
      if (data.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Variant "${variant}" not found`,
          confirmButtonText: 'Back to Dashboard',
        }).then(() => navigate('/'))
        return
      }
      const responseGene = await axios.get(
        `${serviceUrl}/genescores/gene/${response?.data?.Gene.toLocaleUpperCase()}`,
        {
          headers: { 'Content-Type': 'application/json', Accept: '*' },
        }
      )
      const categorizedData = responseGene?.data?.map((item) => {
        let category = null
        for (const [key, values] of Object.entries(categoryMap)) {
          if (values.includes(item.ispp)) {
            category = key
            break
          }
        }
        return { ...item, category }
      })
      console.log(categorizedData)

      const filteredData = categorizedData
        .filter((item) => item.geneScore > 0)
        .sort((a, b) => b.geneScore - a.geneScore)
        .slice(0, 8)

      responsee.data.shift()
      const updatedFilteredData = filteredData.map((item) => {
        const match = responsee.data.find((responseItem) => responseItem.ispp === item.ispp)

        if (match) {
          return {
            ...item,
            score: parseFloat(match.score),
            rankscore: parseFloat(match.rankscore),
          }
        }

        return item
      })
      const updatedFilteredDataa = categorizedData.map((item) => {
        const match = responsee.data.find((responseItem) => responseItem.ispp === item.ispp)

        if (match) {
          return {
            ...item,
            score: parseFloat(match.score),
            rankscore: parseFloat(match.rankscore),
          }
        }

        return item
      })
      console.log(updatedFilteredData, 'updatedFilteredData')
      console.log(updatedFilteredDataa, 'updatedFilteredDataa')
      const isppValuesInFilteredData = updatedFilteredData.map(item => item.ispp);
      const updatedFilteredDataaCleaned = updatedFilteredDataa.filter(
        (item) => !isppValuesInFilteredData.includes(item.ispp)
      )
      console.log(updatedFilteredDataaCleaned, 'updatedFilteredDataaCleaned')
      setBestISPPs(updatedFilteredData)
      setVariantISPPData(updatedFilteredDataaCleaned)
      setVariantData(data)
      var t = categorizeISPPs(updatedFilteredDataa)
      setAllISPPData(responsee.data)
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Error fetching data for variant "${variant}"`,
        confirmButtonText: 'Back to Dashboard',
      }).then(() => navigate('/'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVariantData()
  }, [variant])
  if (loading) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='d-flex justify-content-center'>
            <div className='spinner-border' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }
  console.log(variantISPPData)
  return (
    <div className='card'>
      <PageTitle breadcrumbs={[]} Gene={variantData?.Gene}>{`${variant}(${variantData?.Gene || ''
        })`}</PageTitle>
      <div className='card-body'>
        {variantData && (
          <div>
            <div
              className='d-flex justify-content-between align-items-center  w-full'
              id='page-had'
            >
              <div
                className=' rounded    py-3 px-6 me-6 mb-3'
                style={{
                  minWidth: '400px',
                  border:
                    '1px dashed' +
                    (variantData?.VAMPP_score_ranked > 0.390
                      ? '#e93645'
                      : variantData?.VAMPP_score_ranked < 0.217
                        ? '#97DFB5'
                        : '#000'),
                }}
              >
                <div className='d-flex gap-5 mt-2'>
                  <div className='fw-bold fs-1 '>
                    VAMPP-score: {variantData?.VAMPP_score_ranked.toFixed(3) || 0}
                  </div>
                </div>
                <div
                  className={`fw-bold fs-6 mt-2 text-${variantData?.VAMPP_score_ranked > 0.390
                    ? 'danger'
                    : variantData?.VAMPP_score_ranked < 0.217
                      ? 'success'
                      : 'dark'
                    }`}
                >
                  {variantData?.VAMPP_score_ranked > 0.39 ? (
                    <>
                      <span>ACMG criteria recommendation: PP3-moderate</span>
                      <i
                        className='fa fa-info-circle ms-2'
                        data-tooltip-id='desc-tooltip'
                        data-tooltip-html={`Consider adding PP3 evidence at a moderate level based on ISPP results. The VAMPP-score identifies this variant as pathogenic, <br>with the threshold defined by the VAMPP-score’s distribution. Scores above the <br>95th quantile of unknown variants in ClinVar align more closely with known pathogenic variants.`}
                        style={{
                          fontSize: '12px',
                          color: 'grey',
                        }}
                      ></i>
                    </>
                  ) : variantData?.VAMPP_score_ranked < 0.217 ? (
                    <>
                      <span>ACMG criteria recommendation: BP4-strong</span>
                      <i
                        className='fa fa-info-circle ms-2'
                        data-tooltip-id='desc-tooltip'
                        data-tooltip-html={`Consider adding BP4 evidence at a strong level based on ISPP results. The VAMPP-score identifies this variant as benign,<br> with the threshold defined by the VAMPP-score’s distribution. Scores below the<br> 95th quantile of known benign variants in ClinVar indicate benignity.`}
                        style={{
                          fontSize: '12px',
                          color: 'grey',
                        }}
                      ></i>
                    </>

                  ) : (
                    <>
                      <span>
                        No ACMG criteria adjustment for multiple in silico evidence.
                      </span>
                      <i
                        className='fa fa-info-circle ms-2'
                        data-tooltip-id='desc-tooltip'
                        data-tooltip-html={`Consider adding PP3 or BP4 evidence at the default supporting level based on ISPP results.<br> We recommend using the best-performing ISPPs for variants in the gene <i>${variantData?.Gene || ''}</i> to ensure<br> accurate predictions and their agreement as multiple in silico evidence for PP3/BP4.`}
                        style={{
                          fontSize: '12px',
                          color: 'grey',
                        }}
                      ></i>
                    </>
                  )}


                </div>
              </div>
              <div className=''>
                {/* {bestISPPs.length === 8 && (
                  <p className='text-end mt-2'>
                    For gene <label className='fst-italic'> {variantData?.Gene} </label> , these are
                    the most accurate ISPPs based on VAMPP-score metrics.
                    <br /> We recommend using these for the in-silico interpretation of the variants
                    in gene <label className='fst-italic'> {variantData?.Gene} </label>. Please
                    check About section for more info on our metrics.
                  </p>
                )} */}
                {bestISPPs.length === 0 && (
                  <p className='text-end mt-2'>
                    For gene <label className='fst-italic'> {variantData?.Gene} </label>, it has
                    been evaluated that there are not specific ISPPs with prediction accuracy,{' '}
                    <br />
                    which might be due to limited data availability. However, these are the most
                    accurate ISPPs based on our metrics, for both exome- and Mendeliome-wide. We
                    recommend using these for the in-silico interpretation of the variants in the
                    gene <label className='fst-italic'> {variantData?.Gene} </label> when in doubt.
                    Please check About section for more info on our metrics.
                  </p>
                )}
          
              </div>
            </div>
            {/* header columns    */}
            <div
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                // Yatay kaydırma için
                width: '100%', // Ekran genişliğine göre
                whiteSpace: 'nowrap', // İçeriği aynı satırda tutmak için
              }}
            >
              <div className='d-flex justify-content-end mt-1' id='table-columns'>
                <div
                  className='col-md-12'
                  style={{
                    backgroundColor: 'white',
                    padding: '15px',
                    textAlign: 'left',
                    margin: '5px 10px',
                    position: 'relative',
                    fontWeight: '500',
                    borderLeft: `10px solid white`,
                    borderRight: `10px solid white`,
                    borderBottom: `1px solid #f0f0f0`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  <div
                    className='table-header-wrapper d-flex justify-content-between'
                    style={{
                      marginLeft: '35px',
                    }}
                  >
                    {[
                      { label: 'ISPP', key: 'ISPP', desc: ' in silico pathogenicity predictor' },
                      { label: 'Prediction Accuracy', key: 'predictionAccuracy', desc: 'Combines metrics to assess ISPP performance for each gene, <br>with lower scores indicating weaker accuracy and negative scores reflecting severe misclassification, between benign and pathogenic variants.' },
                      {
                        label: 'Score', key: 'score', desc: 'The raw score from an ISPP, indicating how likely a variant is pathogenic based on the ISPP’s scoring algorithm.',
                      },
                      { label: 'Rank Score', key: 'rankScore', desc: 'The scaled version of the raw score (0-to-1), with values near 0 indicating benignity and values near 1 indicating pathogenicity.' },
                      { label: 'Category', key: 'category', desc: 'ISPP classification based on methodology: Nucleotide Conservation for evolutionary sequence conservation,<br> Amino Acid Conservation to evaluate protein changes, Multi-Parameter integrates diverse data,<br> and Multi-Score combines multiple predictors to improve accuracy.' },
                    ].map((column) => (
                      <div
                        className='col-md-2 table-header'
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                      >

                        <strong>
                          {column.key === 'ISPP' && (
                            <div className="" style={{
                              minWidth: '25px',
                              display: 'inline-block',
                            }}> </div>
                          )}
                          <img
                            src='/sort-triangle.png'
                            alt='sort-icon'
                            style={{
                              width: '12px',
                              height: '12px',
                              marginRight: '5px',
                              transform: sortStates[column.key] === 1 ? 'rotate(90deg)' : 'none',
                              visibility: sortStates[column.key] ? 'visible' : 'hidden',
                            }}
                          />
                          {column.label}{' '}
                          <i
                            className='fa fa-info-circle mx-2'
                            data-tooltip-id='desc-tooltip'
                            data-tooltip-place='bottom'
                            data-tooltip-html={column.desc}
                            style={{ fontSize: '12px', color: 'gray' }}
                          ></i>
                        </strong>
                      </div>
                    ))}
                    {sortStates.predictionAccuracy !== 1 && (
                      <button
                        className='bg-white  position-sticky my-2  back-to-default-sort '
                        style={{
                          right: '10px',
                          border: '1px solid #d5d4d4',
                          borderRadius: '5px',
                          top: '0px',
                        }}
                        data-tooltip-id='desc-tooltip'
                        data-tooltip-html={`Reset to default sort`}
                        onClick={() =>
                          setSortStates({
                            ISPP: 0,
                            predictionAccuracy: 1, // Default olarak burası 1 olacak
                            multipleComparisonValue: 0,
                            pairwiseComparisonValue: 0,
                            category: 0,
                          })
                        }
                      >
                        <i className='fa fa-repeat'></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div
                style={{ maxHeight: maxHeight, display: 'flex', flexDirection: 'column', minWidth: '1200px', overflowY: 'auto', overflowX: 'hidden' }}
                className='  rounded mt-3'
                ref={containerRef}
              >
                <div className="mt-1">
                  <div className="d-flex mb-4 position-relative">
                    <div className=' position-relative  sticky-header' style={{
                      borderRight: '4px solid #2ecc71',
                      borderRadius: '6px',
                      height: 'auto',
                    }}>
                      <span className="list-title  fw-bold fs-2 position-sticky" style={{
                        top: 0,
                      }} >   Best ISPP </span>
                    </div>
                    <div
                      style={{
                        width: '97.5%',
                        marginLeft: 'auto',
                      }}
                      ref={bestIsppRef}
                    >
                      {bestISPPs.map((item, index) => (
                        <div key={index} className='' style={{
                          cursor: 'pointer',
                        }}
                        >
                          <div
                            style={{
                              borderLeft: `30px solid ${item.geneScore < 0 ? '#d3d3d3' : categoryColors[item.category]}`,
                              borderRight: `10px solid ${item.geneScore < 0 ? '#d3d3d3' : categoryColors[item.category]}`,
                              borderBottom: `1px solid #f0f0f0`,
                              borderTop: `1px solid #f0f0f0`,
                            }}
                            className='list-wrapper'
                            onClick={() => handleClickISPP(item.ispp, item.rankscore)}
                          >
                            <div
                              style={{
                                fontSize: '16px',
                                fontWeight: 'bold',
                              }}
                              className='col-md-2 d-flex'
                            >
                              <i className={index < 3 ? 'fa fa-trophy mx-2' : 'fa fa-award mx-2'}
                                data-tooltip-id='desc-tooltip'
                                data-tooltip-html={`Ranked #${index + 1} by VAMPP-score for <i>${variantData?.gene}</i> variants based on accuracy.`}
                                style={{ fontSize: '20px', color: 'gold' }}></i>

                              <div
                                style={{
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                }}
                                className='ispp-cell'

                              >
                                <span
                                  data-tooltip-id='desc-tooltip'
                                  data-tooltip-place='right'
                                  data-tooltip-html={isppDescriptions[item?.ispp]}
                                >
                                  {userReadableISPPMap[item?.ispp] || item?.ispp}
                                </span>
                              </div>
                            </div>
                            <span className='d-block mb-2  fw-bold fs-7 mt-4 col-md-2 list-cell'>
                              {/* Prediction accuracy: */}
                              {item.geneScore?.toFixed(3) || ''}
                            </span>
                            <span className='d-block mb-2  fw-bold fs-7 mt-4 col-md-2 list-cell'>
                              {/* Multiple comparison value:{' '} */}
                              {isNaN(item.score) ? '-' : item.score.toFixed(3) || '-'}
                            </span>
                            <span className='d-block mb-2  fw-bold fs-7 mt-4 col-md-2 list-cell'>
                              {/* Pairwise comparison value:{' '} */}
                              {isNaN(item.rankscore) ? '-' : item.rankscore.toFixed(3) || '-'}
                            </span>
                            <span className='d-block fs-7   col-md-2 list-cell-mid'>
                              <label className="badge badge-pill  text-white
                                " style={{
                                  backgroundColor: item.geneScore < 0 ? '#d3d3d3' : categoryColors[item.category],
                                }}>
                                {item.category === 'A' && 'Nucleotide Conservation'}
                                {item.category === 'B' && 'Amino Acid Conservation'}
                                {item.category === 'C' && 'Multi-Parameter'}
                                {item.category === 'D' && 'Multi-Score'}
                              </label>
                            </span>
                            {/* <Dropdown1
                        plotError={plotError}
                        itemName={item.ispp}
                        selectedISPP={selectedISPP}
                        data={violinData}
                      />
                      <Tooltip id='box-tooltip' place={'right'} /> */}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className='mt-1'>
                  <div className='mb-4 d-flex  '>
                    <div className=' position-relative sticky-header' style={{
                      borderRight: '4px solid #EBEDF1',
                      borderRadius: '6px',
                      height: 'auto',
                    }}>
                      <span className="list-title  fw-bold fs-2 position-sticky" style={{
                        top: 0,
                      }} >   Other ISPP </span>
                    </div>
                    <div
                      style={{
                        width: '97.5%',
                        marginLeft: 'auto',
                      }}

                      ref={advancedRef}
                    >
                      {variantISPPData.map((item, index) => {
                        if (hidenegative && item.geneScore < 0) return null
                        const maxGeneScore = Math.max(
                          ...variantISPPData.map((item) => item.geneScore || 0)
                        ) // En yüksek geneScore değeri


                        return (
                          <>
                            <div
                              key={index}
                              style={{
                                cursor: 'pointer',
                              }}
                            >
                              <div
                                style={{
                                  borderLeft: `30px solid ${item.geneScore < 0 ? '#d3d3d3' : categoryColors[item.category]}`,
                                  borderRight: `10px solid ${item.geneScore < 0 ? '#d3d3d3' : categoryColors[item.category]}`,
                                  borderBottom: `1px solid #f0f0f0`,
                                  borderTop: `1px solid #f0f0f0`,

                                }}
                                className='list-wrapper'
                                onClick={() => handleClickISPP(item.ispp, item.rankscore)}
                              >
                                <div
                                  style={{
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                  }}
                                  className='col-md-2 d-flex'
                                >
                                  <div
                                    style={{
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      marginLeft: '25px',
                                    }}
                                    className='ispp-cell'

                                  >
                                    <span
                                      data-tooltip-id='desc-tooltip'
                                      data-tooltip-place='right'
                                      data-tooltip-html={isppDescriptions[item?.ispp]}
                                    >
                                      {userReadableISPPMap[item?.ispp] || item?.ispp}
                                    </span>
                                  </div>
                                </div>
                                <span className='d-block fw-bold col-md-2 mb-2 list-cell'>
                                  {item.geneScore?.toFixed(3) || ''}
                                </span>
                                <span className='d-block fw-bold col-md-2 mb-2 list-cell'>

                                  {/* Multiple comparison value:{' '} */}
                                  {isNaN(item.score) ? '-' : item.score.toFixed(3) || '-'}
                                </span>
                                <span className='d-block fw-bold col-md-2 mb-2 list-cell'>

                                  {/* Pairwise comparison value:{' '} */}
                                  {isNaN(item.rankscore) ? '-' : item.rankscore.toFixed(3) || '-'}
                                </span>
                                <span className='d-block fs-7   col-md-2 list-cell-mid'>
                                  <label className="badge badge-pill  text-white
                                " style={{
                                      backgroundColor: item.geneScore < 0 ? '#d3d3d3' : categoryColors[item.category],
                                    }}>
                                    {item.category === 'A' && 'Nucleotide Conservation'}
                                    {item.category === 'B' && 'Amino Acid Conservation'}
                                    {item.category === 'C' && 'Multi-Parameter'}
                                    {item.category === 'D' && 'Multi-Score'}
                                  </label>
                                </span>

                              </div>
                            </div>
                          </>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <Modal
          isOpen={selectedISPP === null ? false : true}
          modalTitle={selectedISPP}
          onClose={() => setSelectedISPP(null)}
        >
          {plotError && <div className='text-center py-2 text-danger'> {plotError} </div>}
          {!violinData && !plotError && (
            <div className='d-flex justify-content-center py-2'>
              <div className='spinner-border text-center' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          )}
          {violinData && <ViolinPlot gene={variant} ispp={selectedISPP} rankScore={selectedRankScore} data={violinData} />}
        </Modal>
      </div>
      <Tooltip id='desc-tooltip' place={'right'} />
      <Tooltip id='box-tooltip' place={'right'} />
      <Tooltip id='advanced-tooltip' place={'right'} />
    </div>
  )
}

export default VariantSearchPage
